import React, { Fragment, useEffect } from "react"
import { graphql } from "gatsby"
import Helmet from "react-helmet"

import { Layout } from "../../components/Layout"
import { AffiliateList } from "../../components/AffiliateList"
import { SEO } from "../../components/SEO"
import { ReviewsCMS } from "../../components/ReviewsCMS"
import { BeforeAndAfters } from "../../components/BeforeAndAfters"
import { Tab } from "../../components/Tab"
import { ClinicalDataWide } from "../../components/ClinicalDataWide"
import { BlephTypes } from "../../components/BlephTypes"
import { FunctionalBleph } from "../../components/FunctionalBleph"
import { ConsultingLocations } from "../../components/ConsultingLocations"
import { ScarringSection } from "../../components/ScarringSection"

import styles from "./product.module.css"
import containerClasses from "../../styles/container.module.css"
import buttons from "../../styles/buttons.module.css"

import doctify from "../../assets/images/doctify-logo.svg"
import check32 from "../../assets/icons/check32.svg"
import shieldTrusted from "../../assets/icons/shield-trusted.svg"
import awardTrophy from "../../assets/icons/award-trophy.svg"
import bespokeTailor from "../../assets/icons/bespoke-tailor.svg"
import advancedTech from "../../assets/icons/advanced-tech.svg"
import starReview from "../../assets/images/star_review.svg"
import check from "../../assets/icons/check.svg"
import surgicalScrubs from "../../assets/images/elizabeth-surgical-scrubs.avif"

// --- Schema helpers -----------------------------------------------------
// Strip HTML tags and collapse whitespace, returning plain text suitable
// for FAQPage answer text.
function stripHtml(html) {
  if (!html) return ""
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&#x?[0-9a-f]+;/gi, " ")
    .replace(/\s+/g, " ")
    .trim()
}

// Extract Q&A pairs from the <details>/<summary>/<h3> structure used in
// the bottomBody of CMS markdown files.
function extractFaqs(html) {
  if (!html) return []
  const faqs = []
  // Non-greedy match; tolerates nested <details> by stopping at the first
  // </details> that follows the question.
  const regex =
    /<details[^>]*>\s*<summary>[\s\S]*?<h3>([\s\S]*?)<\/h3>[\s\S]*?<\/summary>([\s\S]*?)<\/details>/gi
  let m
  while ((m = regex.exec(html)) !== null) {
    const question = stripHtml(m[1])
    // For the answer, also drop any nested <details>...</details> blocks
    // so we don't include child questions in a parent answer.
    const answerHtml = m[2].replace(/<details[^>]*>[\s\S]*?<\/details>/gi, " ")
    const answer = stripHtml(answerHtml)
    if (question && answer) faqs.push({ question, answer })
  }
  return faqs
}

// Clamp star rating to the 1–5 range Google expects.
function normalizeStars(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  return Math.min(5, Math.max(1, n))
}

function PabauFormEmbed() {
  useEffect(() => {
    if (typeof window === "undefined") return

    // Initialise into this page's #c1125. Must run on every mount, not just
    // the first: each client-side navigation renders a fresh container, and
    // skipping re-init left the fallback text where the form should be.
    const initialize = () => {
      try {
        const options = {
          id: "RuCZbg",
          container: "c1125",
          height: "379px",
          form: "//forms.pabau.com/index.php?r=app%2Fembed",
        }

        if (window.EasyForms) {
          new window.EasyForms().initialize(options).display()
        }
      } catch (e) {}
    }

    const existing = document.querySelector(
      'script[data-pabau-widget="RuCZbg"]'
    )
    if (existing) {
      // Script already on the page from a previous route: init directly,
      // or wait for it if it is still loading.
      if (window.EasyForms) initialize()
      else existing.addEventListener("load", initialize)
      return
    }

    const script = document.createElement("script")
    script.type = "text/javascript"
    script.src = "https://forms.pabau.com/static_files/js/form.widget.js"
    script.async = true
    script.dataset.pabauWidget = "RuCZbg"
    script.onload = initialize

    document.body.appendChild(script)
  }, [])

  return (
    <div id="c1125">
      Fill out my{" "}
      <a href="https://forms.pabau.com/index.php?r=app%2Fform&id=RuCZbg">
        online form
      </a>
      .
    </div>
  )
}

export default function Template({ data }) {
  const { markdownRemark } = data
  const { frontmatter, html } = markdownRemark
  const {
    title,
    h1,
    excerpt,
    image,
    imageAlt,
    imageTitle,
    type,
    seoTitle,
    metaDescription,
    bottomBody,
    reviews,
    tabs,
    treatmentAreas,
    treatmentAreasImage,
    concernAreas,
    concernAreasImage,
    beforeAndAfters,
    path,
    clinicalData,
    lastClinicalReview,
    safetyLogos,
    blephTypes,
    functionalBleph,
    suitability,
    showLocations,
    scarring,
  } = frontmatter

  const isBlepharoplastyPage =
    path === "/oculoplastic-services/blepharoplasty-surgery/"

  // Anchor links can target an individual FAQ (e.g. #faq-scarring). A closed
  // <details> would leave the answer hidden after the jump, so expand the
  // target accordion on load and on in-page hash navigation.
  useEffect(() => {
    const openTargetDetails = () => {
      const hash = window.location.hash
      if (!hash) return
      let target
      try {
        target = document.querySelector(hash)
      } catch (e) {
        return
      }
      const details = target && target.closest("details")
      if (details) details.open = true
    }
    openTargetDetails()
    window.addEventListener("hashchange", openTargetDetails)
    return () => window.removeEventListener("hashchange", openTargetDetails)
  }, [])

  const physicianEntity = {
    "@type": "Physician",
    "@id": "https://drelizabethhawkes.com/#physician",
    name: "Dr Elizabeth Hawkes",
    identifier: {
      "@type": "PropertyValue",
      name: "GMC Number",
      value: "7049736",
    },
    hasCredential: [
      {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: "FRCOphth",
        name: "Fellow of the Royal College of Ophthalmologists",
      },
      {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: "MBBS",
        name: "Bachelor of Medicine, Bachelor of Surgery",
      },
      {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: "BSc",
        name: "Bachelor of Science",
      },
    ],
    medicalSpecialty: [
      "https://schema.org/Ophthalmology",
      "Oculoplastic Surgery",
      "Aesthetic Medicine",
    ],
  }

  // ----- FAQPage schema -------------------------------------------------
  const faqs = extractFaqs(bottomBody)
  const faqSchema =
    faqs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `https://drelizabethhawkes.com${path}#faq`,
          mainEntity: faqs.map(({ question, answer }) => ({
            "@type": "Question",
            name: question,
            acceptedAnswer: { "@type": "Answer", text: answer },
          })),
        }
      : null

  // ----- Reviews + AggregateRating on the Physician ---------------------
  // Attached to the same @id as the site-wide Physician entity emitted by
  // SEO.tsx so Google merges them into a single graph node.
  const validReviews = (reviews || []).filter(
    (r) => r && r.message && normalizeStars(r.stars) !== null
  )

  const aggregateRating =
    validReviews.length > 0
      ? {
          "@type": "AggregateRating",
          ratingValue: (
            validReviews.reduce((sum, r) => sum + normalizeStars(r.stars), 0) /
            validReviews.length
          ).toFixed(2),
          bestRating: "5",
          worstRating: "1",
          ratingCount: validReviews.length,
          reviewCount: validReviews.length,
        }
      : null

  // Cap the inline review list at 25 to keep payload reasonable; the
  // aggregateRating still reflects all reviews on the page.
  const reviewSchemaList = validReviews.slice(0, 25).map((r) => ({
    "@type": "Review",
    author: { "@type": "Person", name: r.name || "Verified Patient" },
    datePublished: r.date,
    reviewRating: {
      "@type": "Rating",
      ratingValue: normalizeStars(r.stars),
      bestRating: "5",
      worstRating: "1",
    },
    reviewBody: r.message,
    itemReviewed: {
      "@type": "MedicalProcedure",
      name: title,
    },
  }))

  const physicianReviewsSchema = aggregateRating
    ? {
        "@context": "https://schema.org",
        "@type": "Physician",
        "@id": "https://drelizabethhawkes.com/#physician",
        name: "Dr Elizabeth Hawkes",
        aggregateRating,
        review: reviewSchemaList,
      }
    : null

  const medicalWebPageSchema = isBlepharoplastyPage
    ? {
        "@context": "https://schema.org",
        "@type": "MedicalWebPage",
        "@id":
          "https://drelizabethhawkes.com/oculoplastic-services/blepharoplasty-surgery/#webpage",
        name: "Blepharoplasty: Eyelid Surgery | London – Dr Elizabeth Hawkes",
        url: "https://drelizabethhawkes.com/oculoplastic-services/blepharoplasty-surgery/",
        description:
          "Miss Elizabeth Hawkes performs blepharoplasty surgery at the award-winning Cadogan Clinic in London. Schedule your consultation today.",
        priceRange: "££££",
        about: {
          "@type": "MedicalProcedure",
          name: "Blepharoplasty",
          alternateName: "Eyelid Surgery",
          description:
            "Surgical removal of excess skin and fat from the eyelids to improve appearance, reduce under-eye bags and create a more youthful look.",
          procedureType: "https://schema.org/SurgicalProcedure",
          bodyLocation: "Eyelids",
        },
        author: physicianEntity,
        reviewedBy: physicianEntity,
        provider: {
          "@type": ["Physician", "MedicalBusiness"],
          "@id": "https://drelizabethhawkes.com/#physician",
          name: "Dr Elizabeth Hawkes",
          priceRange: "££££",
          address: {
            "@type": "PostalAddress",
            streetAddress: "120 Sloane St",
            addressLocality: "London",
            addressRegion: "Chelsea",
            postalCode: "SW1X 9BW",
            addressCountry: "GB",
          },
        },
      }
    : null

  // ----- BreadcrumbList ---------------------------------------------------
  // Generic across every product page so SERP breadcrumb display is eligible
  // on all services / conditions, not just bleph.
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://drelizabethhawkes.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name:
          type === "oculoplasticServices"
            ? "Oculoplastic Services"
            : type === "aestheticServices"
            ? "Aesthetic Services"
            : type === "conditions"
            ? "Conditions"
            : "Services",
        item:
          type === "oculoplasticServices"
            ? "https://drelizabethhawkes.com/oculoplastic-services/"
            : type === "aestheticServices"
            ? "https://drelizabethhawkes.com/aesthetic-services/"
            : type === "conditions"
            ? "https://drelizabethhawkes.com/eye-conditions/"
            : "https://drelizabethhawkes.com/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: title,
        item: `https://drelizabethhawkes.com${path}`,
      },
    ],
  }

  // Hotjar showed patients hunting for before & afters, so the secondary
  // hero CTA anchors there; pages without that section fall back to the
  // FAQs, and pages with neither drop the second button.
  const secondaryHeroCta =
    beforeAndAfters && beforeAndAfters.length > 0
      ? { href: "#before-and-afters", label: "Before & afters" }
      : bottomBody
      ? { href: "#faqs", label: "FAQs" }
      : null

  // Gatsby's router resets scroll on hash navigation (same workaround as
  // team.js), so in-page anchors scroll directly and leave the URL alone.
  // Lazy-loaded images above the target shift the layout while the scroll
  // is in flight, leaving the landing short of the section — so re-align
  // once things settle, unless the user has started scrolling themselves.
  const scrollToAnchor = e => {
    const el = document.getElementById(
      e.currentTarget.getAttribute("href").slice(1)
    )
    if (!el) return
    e.preventDefault()
    el.scrollIntoView({ behavior: "smooth" })
    let cancelled = false
    const cancel = () => {
      cancelled = true
    }
    window.addEventListener("wheel", cancel, { once: true, passive: true })
    window.addEventListener("touchstart", cancel, { once: true, passive: true })
    let tries = 0
    const settle = window.setInterval(() => {
      tries += 1
      const off = Math.abs(el.getBoundingClientRect().top - 120)
      if (cancelled || off <= 16 || tries >= 8) {
        if (!cancelled && off > 16) el.scrollIntoView()
        window.clearInterval(settle)
      }
    }, 700)
  }

  return (
    <Layout page={type}>
      <SEO
        title={seoTitle || title}
        description={metaDescription || excerpt}
        image={image}
        pathname={path}
      />
      <Helmet>
        <link rel="preload" href={image} as="image" />
        {isBlepharoplastyPage && (
          <script>{`(function(h,o,t,j,a,r){h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};h._hjSettings={hjid:3002941,hjsv:6};a=o.getElementsByTagName('head')[0];r=o.createElement('script');r.async=1;r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;a.appendChild(r);})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`}</script>
        )}
        {medicalWebPageSchema && (
          <script type="application/ld+json">
            {JSON.stringify(medicalWebPageSchema)}
          </script>
        )}
        {breadcrumbSchema && (
          <script type="application/ld+json">
            {JSON.stringify(breadcrumbSchema)}
          </script>
        )}
        {faqSchema && (
          <script type="application/ld+json">
            {JSON.stringify(faqSchema)}
          </script>
        )}
        {physicianReviewsSchema && (
          <script type="application/ld+json">
            {JSON.stringify(physicianReviewsSchema)}
          </script>
        )}
      </Helmet>

      <div className="bg-white">
        <div
          className={`${containerClasses.container} pt-12 pb-8 md:pt-24 md:pb-16 lg:pb-12`}
        >
          <div className="col-span-4 md:col-span-8 lg:col-span-6">
            <p className="capitalize overline mb-4 md:mb-6">
              {type.replace(/([a-z])([A-Z])/g, "$1 $2")}
            </p>
            <h1 className={`mt-0 mb-6 lg:mb-8 ${styles.heroTitle}`}>{h1 || title}</h1>
            <div className="flex items-center flex-wrap gap-y-1">
              <div
                className="flex flex-shrink-0 mr-2 md:mr-3"
                aria-label="Five star rating"
                role="img"
              >
                <img loading="lazy" className="h-4 md:h-5 -mr-1" src={starReview} alt="" />
                <img loading="lazy" className="h-4 md:h-5 -mr-1" src={starReview} alt="" />
                <img loading="lazy" className="h-4 md:h-5 -mr-1" src={starReview} alt="" />
                <img loading="lazy" className="h-4 md:h-5 -mr-1" src={starReview} alt="" />
                <img loading="lazy" className="h-4 md:h-5" src={starReview} alt="" />
              </div>
              <p className={`mb-0 mr-2 whitespace-no-wrap text-mainBrand-6 reviewsRow`}>
                <a
                  href="https://www.doctify.com/uk/specialist/ms-elizabeth-dolatshany-hawkes"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="reviewsLink"
                >
                  100+ <span className="hidden md:inline">5-star </span>reviews
                </a>{" "}
                on
              </p>
              <img loading="lazy" className="flex-shrink-0 h-5 md:h-6" src={doctify} alt="Doctify" />
            </div>
          </div>

          <div className="col-span-4 md:col-span-8 lg:col-span-5 lg:col-start-8">
            <p className={`mt-6 lg:mt-0 mb-6 ${styles.heroExcerpt} ${styles.heroExcerptBaseline}`}>{excerpt}</p>
            <div className="flex flex-row flex-wrap -mt-2">
              <a
                className={`flex items-center justify-center text-center flex-1 lg:h-12 mt-2 ${
                  secondaryHeroCta ? "mr-2" : ""
                } ${buttons.btnLargeExpressive} ${buttons.btnCompact}`}
                href="#book"
                onClick={scrollToAnchor}
              >
                <span className="block whitespace-no-wrap">Book now&nbsp;&nbsp;→</span>
              </a>
              {secondaryHeroCta && (
                <a
                  className={`flex items-center justify-center text-center flex-1 lg:h-12 mt-2 ${buttons.btnLargeExpressiveOutline} ${buttons.btnCompact}`}
                  href={secondaryHeroCta.href}
                  onClick={scrollToAnchor}
                >
                  <span className="block whitespace-no-wrap">{secondaryHeroCta.label}&nbsp;&nbsp;↓</span>
                </a>
              )}
            </div>
          </div>

        </div>
      </div>

      <div className="headerHalfAndHalf">
        <div className={containerClasses.container}>
          <div className={`col-span-4 md:col-span-8 lg:col-span-12 relative ${styles.hero}`}>
            <div className={styles.heroBlendTop} aria-hidden="true" />
            <img
              className="relative object-cover w-full h-full"
              src={image}
              alt={imageAlt}
              title={imageTitle}
            />
          </div>
          {/* Clinical Data */}
          {clinicalData && (
            <div className="col-span-4 md:col-span-8 lg:col-span-12">
              <ClinicalDataWide
                overline={title}
                heading={clinicalData.heading}
                tabs={clinicalData.tabs}
              />
            </div>
          )}
        </div>
      </div>

      <div className="bg-white">
        <div
          className={`${containerClasses.container} py-16 md:py-20 lg:py-32 xl:py-40`}
        >
          {/* Heading column — left half on lg+, on top on mobile */}
          <div className="col-span-4 md:col-span-6 md:col-start-2 lg:col-span-6 lg:col-start-1 mb-6 lg:mb-0">
            <h2 className="mt-0 overline">What Is {title}?</h2>
            <p className="mt-2 mb-0 fluid-heading-05 leading-tight">
              Blepharoplasty (Eyelid Surgery) is a surgical procedure typically
              performed to <i>improve the cosmetic appearance</i> of the
              eyelids.
            </p>
          </div>
          {/* Body column — right (1 col gutter), fills the rest on lg+;
              top padding on lg+ baseline-aligns the first body line with
              the statement's first line. */}
          <div className={`col-span-4 md:col-span-6 md:col-start-2 lg:col-span-5 lg:col-start-8 ${styles.whatIsBodyBaseline}`}>
            <div
              className={`${styles.productContent}`}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          </div>
        </div>
      </div>

      {/* Why choose Dr Hawkes — bleph page only */}
      {isBlepharoplastyPage && (
        <div className="bg-white2">
          <div
            className={`${containerClasses.container} py-20 md:py-28 lg:py-32 xl:py-40`}
          >
            <div className="flex flex-col col-span-4 md:col-span-8 lg:justify-center lg:col-span-5">
              <p className="mt-0 overline">Expertise</p>
              <h2 className="mt-0 mb-6 fluid-heading-05 leading-tight">
                Why Choose <i>Dr Hawkes</i> for Blepharoplasty?
              </h2>
              <ul className="mb-4">
                <li>
                  <div className="flex">
                    <img
                      loading="lazy"
                      className="w-6 h-6 mx-auto mr-2 flex-shrink-0"
                      src={check}
                      alt="Checkmark"
                    />
                    <p className="text-neutral-500">
                      <span className="font-bold text-neutral-600">
                        Head of Oculoplastic Surgery at the Cadogan Clinic:
                      </span>{" "}
                      Leads the department at the internationally renowned
                      private clinic in Chelsea, London.
                    </p>
                  </div>
                </li>
                <li>
                  <div className="flex">
                    <img
                      loading="lazy"
                      className="w-6 h-6 mx-auto mr-2 flex-shrink-0"
                      src={check}
                      alt="Checkmark"
                    />
                    <p className="text-neutral-500">
                      <span className="font-bold text-neutral-600">
                        Mentored by the UK&apos;s founding figure:
                      </span>{" "}
                      Over a decade of mentorship under Professor Richard
                      Collin, the pioneer of UK ophthalmic plastic surgery, and
                      now entrusted with the continuing care of his patients.
                    </p>
                  </div>
                </li>
                <li>
                  <div className="flex">
                    <img
                      loading="lazy"
                      className="w-6 h-6 mx-auto mr-2 flex-shrink-0"
                      src={check}
                      alt="Checkmark"
                    />
                    <p className="text-neutral-500">
                      <span className="font-bold text-neutral-600">
                        Trained at Imperial and Harvard, with experience at
                        Moorfields:
                      </span>{" "}
                      FRCOphth, MBBS, BSc. Fellow and Examiner of the Royal
                      College of Ophthalmologists; member of BOPSS and ESOPRS.
                    </p>
                  </div>
                </li>
                <li>
                  <div className="flex">
                    <img
                      loading="lazy"
                      className="w-6 h-6 mx-auto mr-2 flex-shrink-0"
                      src={check}
                      alt="Checkmark"
                    />
                    <p className="text-neutral-500">
                      <span className="font-bold text-neutral-600">
                        Over 20 years of experience:
                      </span>{" "}
                      Completed ophthalmic surgical training on the prestigious
                      Oxford rotation with experience at Moorfields Eye
                      Hospital, before sub-specialising in oculoplastic
                      surgery.
                    </p>
                  </div>
                </li>
                <li>
                  <div className="flex">
                    <img
                      loading="lazy"
                      className="w-6 h-6 mx-auto mr-2 flex-shrink-0"
                      src={check}
                      alt="Checkmark"
                    />
                    <p className="text-neutral-500">
                      <span className="font-bold text-neutral-600">
                        Field leadership and advisory roles:
                      </span>{" "}
                      Founding Faculty of <i>Aesthetic Ocular Safety</i>,
                      Specialist Advisor to CMAC, and Clinical Advisor to{" "}
                      <i>Aesthetics Journal</i>.
                    </p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="col-span-4 md:col-span-8 lg:col-start-7 lg:col-span-6">
              <div
                className="relative w-full overflow-hidden"
                style={{ paddingBottom: "100%" }}
              >
                <img
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                  alt="Dr Elizabeth Hawkes wearing surgical scrubs, Consultant Oculoplastic Surgeon"
                  src={surgicalScrubs}
                />
                <div
                  className="absolute z-10 bottom-2 right-2 inline-flex items-center px-4 py-3 md:px-6 md:py-4"
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.65)",
                    backdropFilter: "blur(20px) saturate(140%)",
                    WebkitBackdropFilter: "blur(20px) saturate(140%)",
                  }}
                >
                  <div
                    className="flex mr-2 md:mr-3"
                    aria-label="Five star rating"
                    role="img"
                  >
                    <img loading="lazy" className="h-4 md:h-5 -mr-1" src={starReview} alt="" />
                    <img loading="lazy" className="h-4 md:h-5 -mr-1" src={starReview} alt="" />
                    <img loading="lazy" className="h-4 md:h-5 -mr-1" src={starReview} alt="" />
                    <img loading="lazy" className="h-4 md:h-5 -mr-1" src={starReview} alt="" />
                    <img loading="lazy" className="h-4 md:h-5" src={starReview} alt="" />
                  </div>
                  <p className={`mb-0 mr-2 whitespace-no-wrap text-mainBrand-6 hidden md:block reviewsRow`}>
                    <a
                      href="https://www.doctify.com/uk/specialist/ms-elizabeth-dolatshany-hawkes"
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="reviewsLink"
                    >
                      100+ 5-star reviews
                    </a>{" "}
                    on
                  </p>
                  <img loading="lazy" className="h-4 md:h-5" src={doctify} alt="Doctify" />
                </div>
              </div>
              {/* Awards caption — tight under the image, one medal per award */}
              <div className="mt-3">
                {[
                  "Tatler Top Doctor 2025 and 2026",
                  "Listed among the Top 4 UK Eye Surgeons in Tatler's Beauty & Cosmetic Surgery Guide",
                ].map(award => (
                  <p
                    key={award}
                    className="mb-0 mt-1 flex items-start text-sm text-neutral-500 leading-relaxed"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                      className="flex-shrink-0 mr-2 mt-0.5 text-gold600"
                      fill="currentColor"
                    >
                      <path d="M17 10.43V2H7v8.43c0 .35.18.68.49.86l4.18 2.51-.99 2.34-3.41.29 2.59 2.24L9.07 22 12 20.23 14.93 22l-.78-3.33 2.59-2.24-3.41-.29-.99-2.34 4.18-2.51c.31-.18.49-.51.49-.86zm-4 1.8l-1 .6-1-.6V3h2v9.23z" />
                    </svg>
                    <span>{award}</span>
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cosmetic Blepharoplasty Types */}
      {blephTypes && blephTypes.types && blephTypes.types.length > 0 && (
        <BlephTypes heading={blephTypes.heading} overline={blephTypes.overline} subtitle={blephTypes.subtitle} types={blephTypes.types} />
      )}

      {/* Concerns Treated */}
      {concernAreas && concernAreas?.length > 0 && concernAreasImage ? (
        <div className="bg-white">
          <div
            className={`${containerClasses.container} py-16 md:py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-6`}
          >
            {/* Image */}
            <div className="col-span-4 md:col-span-8 lg:col-start-1 lg:col-span-6">
              <img
                loading="lazy"
                className="w-full"
                alt={`Concerns treated with ${title} by Dr Elizabeth Hawkes`}
                src={concernAreasImage}
              />
            </div>

            {/* Text */}
            <div className="flex flex-col w-full col-span-4 md:col-span-8 lg:justify-center lg:col-span-5 lg:col-start-8">
              <h2 className="mt-0 mb-2 overline">Concerns Treated</h2>
              <h3 className="mt-0 mb-6 fluid-heading-05">
                Which Concerns Can Be <i>Addressed?</i>
              </h3>
              <ul>
                {concernAreas.map(({ title: concernTitle, text }, index) => (
                  <li key={index}>
                    <div className="flex items-start w-full">
                      <img
                        loading="lazy"
                        className="w-6 h-6 mx-auto mr-2"
                        src={check}
                        alt="Checkmark"
                      />
                      <p className="flex-1 text-neutral-500">
                        <span className="font-bold text-neutral-600">{concernTitle}:</span>{" "}
                        {text}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ) : null}

      {/* Pillars — bleph page only */}
      {isBlepharoplastyPage && (
        <div className="bg-white">
          <div
            className={`${containerClasses.container} py-16 md:py-20 lg:py-24`}
          >
            <div className="col-span-4 md:col-span-8 lg:col-span-12 mb-12 lg:mb-20">
              <h2 className="mt-0 mb-0 fluid-heading-05 leading-tight max-w-2xl">
                <i>Specialist</i> Care, Recognised Expertise
              </h2>
            </div>

            <div className={`col-span-4 md:col-span-4 lg:col-span-3 ${styles.pillar}`}>
              <img loading="lazy" className="h-8 w-8 mb-6" src={shieldTrusted} alt="" />
              <h3 className="mt-0 mb-3 font-serif italic text-2xl leading-tight">
                Trusted Practitioner
              </h3>
              <p className="mb-0 text-neutral-500 leading-relaxed">
                Highly experienced and serving in multiple advisory roles, Dr
                Hawkes stays at the forefront of industry standards and care.
              </p>
            </div>

            <div className={`col-span-4 md:col-span-4 lg:col-span-3 ${styles.pillar}`}>
              <img loading="lazy" className="h-8 w-8 mb-6" src={awardTrophy} alt="" />
              <h3 className="mt-0 mb-3 font-serif italic text-2xl leading-tight">
                Award-Winning
              </h3>
              <p className="mb-0 text-neutral-500 leading-relaxed">
                Multiple-award recipient including Tatler Top Doctor 2025 and
                2026, and one of the Top 4 UK Eye Surgeons in Tatler&apos;s
                Beauty &amp; Cosmetic Surgery Guide.
              </p>
            </div>

            <div className={`col-span-4 md:col-span-4 lg:col-span-3 ${styles.pillar}`}>
              <img loading="lazy" className="h-8 w-8 mb-6" src={bespokeTailor} alt="" />
              <h3 className="mt-0 mb-3 font-serif italic text-2xl leading-tight">
                Bespoke Treatment
              </h3>
              <p className="mb-0 text-neutral-500 leading-relaxed">
                Using extensive facial and ocular expertise, Dr Hawkes tailors
                each treatment to the individual needs and natural features.
              </p>
            </div>

            <div className={`col-span-4 md:col-span-4 lg:col-span-3 ${styles.pillar}`}>
              <img loading="lazy" className="h-8 w-8 mb-6" src={advancedTech} alt="" />
              <h3 className="mt-0 mb-3 font-serif italic text-2xl leading-tight">
                Advanced Techniques
              </h3>
              <p className="mb-0 text-neutral-500 leading-relaxed">
                State-of-the-art technologies and meticulous care underscore Dr
                Hawkes&rsquo; dedication to continuous innovation, learning and
                excellent results.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Treatment Areas */}
      {treatmentAreas && treatmentAreas?.length > 0 && treatmentAreasImage ? (
        <div className="bg-white">
          <div
            className={`${containerClasses.container} py-16 md:py-20 lg:py-24`}
          >
            {/* Text */}
            <div className="flex flex-col w-full col-span-4 md:col-span-8 lg:justify-center lg:col-span-5">
              <h2 className="mt-0 mb-2 overline">Treatment Areas</h2>
              <h3 className="mt-0 mb-6 fluid-heading-05">
                Which Areas Can Be <i>Treated?</i>
              </h3>

              <ul>
                {treatmentAreas.map(({ title: areaTitle, text }, index) => (
                  <li key={index}>
                    <div className="flex items-start w-full">
                      <img
                        loading="lazy"
                        className="w-6 h-6 mx-auto mr-2"
                        src={check}
                        alt="Checkmark"
                      />
                      <p className="flex-1 text-neutral-500">
                        <span className="font-bold text-neutral-600">{areaTitle}:</span> {text}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Image */}
            <div className="col-span-4 md:col-span-8 lg:col-start-7 lg:col-span-6">
              <img
                loading="lazy"
                className="w-full"
                alt={`${title} treatment areas by Dr Elizabeth Hawkes`}
                src={treatmentAreasImage}
              />
            </div>
          </div>
        </div>
      ) : null}

      {/* Before and afters */}
      <Fragment>
        {beforeAndAfters && beforeAndAfters?.length > 0 ? (
          <div id="before-and-afters" className="bg-white2" style={{ scrollMarginTop: "120px" }}>
            <div className={containerClasses.container}>
              <div className="col-span-4 md:col-span-8 lg:col-span-12">
                <BeforeAndAfters beforeAndAfters={beforeAndAfters} treatmentName={title} />
              </div>
            </div>
          </div>
        ) : null}
      </Fragment>

      {/* Scarring (opt-in per page via frontmatter) */}
      {scarring && <ScarringSection {...scarring} />}

      {/* Suitability Section */}
      {suitability && (
        <div className={`bg-white ${styles.suitabilityContent}`}>
          <div className={`${containerClasses.container} py-20 md:py-28 lg:py-32 xl:py-40`}>
            {/* Image */}
            {suitability.image && (
              <div className="col-span-4 md:col-span-8 lg:col-span-6 flex items-center order-2 lg:order-1">
                <img
                  loading="lazy"
                  className="w-full object-cover object-left"
                  style={{ aspectRatio: '1/1' }}
                  src={suitability.image}
                  alt={suitability.imageAlt || ''}
                />
              </div>
            )}
            {/* Heading + Intro + List */}
            <div className="col-span-4 md:col-span-8 lg:col-span-5 lg:col-start-8 flex flex-col lg:justify-center order-1 lg:order-2">
              <p className="mt-0 mb-4 overline">Suitability</p>
              <h2 className="mt-0 mb-6 fluid-heading-05 leading-tight" dangerouslySetInnerHTML={{ __html: suitability.heading }} />
              <p className="mt-0 mb-6 text-neutral-500 leading-relaxed">{suitability.intro}</p>
              <ul className="pl-0 mt-0">
                {suitability.items.map((item, index) => (
                  <li key={index} className="py-2">
                    <div className="flex items-start">
                      <img
                        loading="lazy"
                        className="w-6 h-6 mr-2 flex-shrink-0 mt-0.5"
                        src={check}
                        alt="Checkmark"
                      />
                      <span className="text-neutral-500">{item}</span>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-4 px-6 py-5 md:px-8 md:py-6 flex items-start gap-4 bg-secondaryBrand-2">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0 mt-0.5">
                  <circle cx="10" cy="10" r="9" stroke="#8A7252" strokeWidth="1.5"/>
                  <path d="M10 9V14" stroke="#8A7252" strokeWidth="1.5" strokeLinecap="round"/>
                  <circle cx="10" cy="6.5" r="0.75" fill="#8A7252"/>
                </svg>
                <p className="mb-0 text-sm leading-relaxed text-gold800">{suitability.outro}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Consulting Locations (opt-in per page via frontmatter) */}
      {showLocations && (
        <ConsultingLocations
          overline="Where we practise"
          heading="Blepharoplasty in London"
          introCopy="Dr Hawkes consults at two locations: the Cadogan Clinic in Chelsea and The London Lauriston Clinic in central London."
        />
      )}

      {/* Client Testimonials */}
      <Fragment>
        {reviews && reviews?.length > 0 ? (
          <div className="bg-white">
            <div className={containerClasses.container}>
              <div className="col-span-4 md:col-span-10 lg:col-span-12">
                <ReviewsCMS reviews={reviews} treatmentName={title} />
              </div>
            </div>
          </div>
        ) : null}
      </Fragment>

      {/* Tabs */}
      {tabs && <Tab tabs={tabs} safetyLogos={safetyLogos} pageType={type} />}

      {/* Frequently Asked Questions */}
      <div id="faqs" style={{ scrollMarginTop: "120px" }}>
        <div className="bg-white2">
          {bottomBody ? (
            <div className={`${containerClasses.container} py-16 md:py-20 lg:py-24`}>
              <div className={`z-10 col-span-4 md:flex md:flex-col md:items-center lg:items-start lg:sticky lg:self-start md:col-span-6 md:col-start-2 lg:col-span-5 ${styles.faqSticky}`}>
                <h2 className="mt-0 fluid-heading-05 lg:text-left md:text-center">
                  Frequently Asked <i>Questions</i>
                </h2>
                <p className="md:text-center lg:text-left text-neutral-500">
                  Our team is here to help with any enquiries. If you have
                  questions, please don&apos;t hesitate to get in touch.
                </p>
                <a
                  className={`text-center block lg:inline-block md:w-auto mt-4 md:mt-2 ${buttons.btnLargeExpressive}`}
                  href="#book"
                  onClick={scrollToAnchor}
                >
                  <span className="block whitespace-no-wrap">Book a consultation&nbsp;&nbsp;→</span>
                </a>
              </div>

              <div className="col-span-4 md:col-span-8 lg:col-span-6 lg:col-start-7">
                <div
                  className={`${styles.productContent} col-span-4 md:col-span-4 lg:col-span-6 `}
                  dangerouslySetInnerHTML={{ __html: bottomBody
                    .replace(/<\/summary>/gi, '</summary><div class="faq-body"><p>')
                    .replace(/<\/details>/gi, '</p></div></details>')
                    .replace(/<br\s*\/?>/gi, '</p><p>')
                    .replace(/(<[^>]*>)|(\d[\d,.\-–\s]*(?:weeks?|days?|hours?|hrs?|mins?|minutes?|months?|years?|%)?)/gi, (match, tag, num) =>
                      tag ? tag : '<strong>' + num + '</strong>'
                    )
                    /* No deduping of nested <strong> pairs here: collapsing
                       "</strong></strong>" without its (non-adjacent) opening
                       pair left an unclosed <strong>, and the parser then
                       bold-wrapped every following FAQ — splitting the list
                       and breaking the summary borders. Nested strongs are
                       valid and render identically. */
                  }}
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div id="book" style={{ scrollMarginTop: "120px" }} className={`gap-0 py-16 md:py-20 lg:py-24 bg-brilliantWhite ${styles.bookAnchor}`}>
        <div className={`${containerClasses.container} gap-0 gap-y-0 gap-x-0`}>
          <div className="col-span-4 md:col-span-8 lg:col-span-12 flex flex-col items-center text-center">
            <h2 className="m-0 fluid-heading-05 leading-tight" style={{ maxWidth: '500px' }}>
              Book a <i>Consultation</i>
            </h2>
            <p className="mt-4 mb-0 text-neutral-500 max-w-md leading-relaxed">
              If you would like to book a consultation, please use the form
              below. We will respond within 2 working days.
            </p>
          </div>

          <div className="col-span-4 mt-0 md:col-span-8 lg:col-span-8 lg:col-start-3">
            <PabauFormEmbed />
            <div className="col-span-4 px-4 mt-0 text-sm text-center text-neutral-500 md:text-base md:col-span-8 lg:col-span-8 lg:col-start-3">
              By submitting the form, I agree to the{" "}
              <a
                href="/privacy-policy/"
                className="text-sm underline md:text-base text-gold600"
              >
                Privacy Policy
              </a>{" "}
              &{" "}
              <a
                href="/terms-and-conditions/"
                className="text-sm underline md:text-base text-gold600"
              >
                Terms & Conditions
              </a>
            </div>
          </div>
        </div>
      </div>

      {lastClinicalReview && (
        <div className="bg-white">
          <div className={`${containerClasses.container} py-6`}>
            <p className="col-span-4 mb-0 text-sm text-neutral-500 md:col-span-8 lg:col-span-12">
              Last Clinical Review &amp; Update: {lastClinicalReview}
            </p>
          </div>
        </div>
      )}

      <AffiliateList />
    </Layout>
  )
}

export const pageQuery = graphql`
  query($path: String!) {
    markdownRemark(frontmatter: { path: { eq: $path } }) {
      html
      frontmatter {
        title
        h1
        excerpt
        image
        imageAlt
        imageTitle
        type
        seoTitle
        metaDescription
        reviews {
          name
          date(formatString: "MMMM DD, YYYY")
          stars
          message
        }
        tabs {
          id
          title
          content
        }
        treatmentAreas {
          title
          text
        }
        treatmentAreasImage
        concernAreas {
          title
          text
        }
        concernAreasImage
        beforeAndAfters {
          image
          imageAlt
        }
        bottomBody
        path
        lastClinicalReview(formatString: "dddd Do MMMM YYYY")
        safetyLogos {
          image
          alt
          role
          name
        }
        clinicalData {
          heading
          tabs {
            id
            title
            rows {
              label
              value
            }
          }
        }
        blephTypes {
          overline
          heading
          subtitle
          types {
            id
            title
            text
            image
            imageAlt
            videoUrl
            videoLinkText
          }
        }
        functionalBleph {
          heading
          text
          points
        }
        suitability {
          heading
          intro
          image
          imageAlt
          listHeading
          items
          outro
        }
        scarring {
          overline
          heading
          paragraphs
          faqLinkText
          faqAnchor
          pairs {
            before
            beforeAlt
            after
            afterAlt
            caption
          }
        }
        showLocations
      }
    }
  }
`
